import { cn } from '@sr/ui-library';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useMatches } from 'react-router-dom';
import { MembershipApplicationGate } from '../components/MembershipApplicationGate';
import { MobileHeader } from '../components/MobileHeader';
import { PageHeaderActionsSlot } from '../components/PageHeaderActionsSlot';
import { ReturnToMsr2Banner } from '../components/ReturnToMsr2Banner';
import { Sidebar } from '../components/Sidebar';
import { MSR3_BODY_CLASS, useMsr3ThemeBody } from '../hooks/useMsr3ThemeBody';
import type { SidebarVariant } from '../types/sidebar';
import { documentTitleFromMatches } from '../utils/helpers/routeTitle';

export type MSR3BaseLayoutProps = {
  classes?: {
    childrenWrapper?: string;
  };
  /**
   * Sticky page chrome (e.g. `PageHeader`). When set, main content is wrapped in
   * the standard `container mb-10 space-y-10` block so pages do not repeat it.
   */
  pageHeader?: ReactNode;
  /** Overrides the sidebar logo's workspace-derived destination. */
  sidebarLogoHref?: string;
  sidebarVariant?: SidebarVariant;
} & React.PropsWithChildren;

/**
 * Base layout for all MSR3 routes.
 * Provides the Sidebar component globally across all MSR3 routes.
 * This layout wraps all MSR3 routes and ensures consistent navigation.
 *
 * - Desktop (md and above): Sidebar is always visible
 * - Mobile (below md): Sidebar is hidden, accessible via hamburger menu
 */
export const MSR3BaseLayout = ({
  children,
  classes,
  pageHeader,
  sidebarLogoHref,
  sidebarVariant = 'workspace',
}: MSR3BaseLayoutProps) => {
  const matches = useMatches();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const documentTitle = documentTitleFromMatches(matches);

  useMsr3ThemeBody();

  const handleToggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  return (
    <PageHeaderActionsSlot>
      <div
        className={cn(
          MSR3_BODY_CLASS,
          'flex h-screen w-full flex-col md:flex-row',
        )}
      >
        <Helmet>
          <title>{documentTitle}</title>
        </Helmet>
        <MobileHeader
          isSidebarOpen={isSidebarOpen}
          onMenuToggle={handleToggleSidebar}
        />

        <Sidebar
          logoHref={sidebarLogoHref}
          open={isSidebarOpen}
          variant={sidebarVariant}
          onOpenChange={setIsSidebarOpen}
        />

        {/* scroll-pt keeps focus/anchor scrolls clear of the sticky PageHeader. */}
        <main className="flex grow scroll-pt-32 flex-col overflow-y-auto [scrollbar-gutter:stable]">
          {/* Width copied from the children wrapper below so the banner lines up
            with page content. Above the sticky header, so it scrolls away. */}
          <ReturnToMsr2Banner className="container mt-3 max-md:px-4" />

          {pageHeader}

          {/* pt-2 pairs with PageHeader's pb-5 — only when there's a header to
            clear, so header-less pages aren't pushed down. */}
          <div
            className={cn(
              'container mb-10 mt-3 max-md:px-4',
              pageHeader && 'pt-2',
              classes?.childrenWrapper,
            )}
          >
            {children ?? <Outlet />}
          </div>
        </main>

        {/* Site-wide membership modal for ApplicationSent status. Shared with
          WorkspaceLayout so the prompt is not ?v= dependent. Renders nothing
          outside a workspace route. */}
        <MembershipApplicationGate />
      </div>
    </PageHeaderActionsSlot>
  );
};
